/**
 * WebRTC Insertable Streams ile ses/görüntü/ekran çerçevelerini
 * AES-256-GCM (uygulama katmanı) şifreler.
 *
 * MEDIA_FRAME_ENCRYPTION_ENABLED = false iken tamamen devre dışı:
 * encodedInsertableStreams açılmaz, transform bağlanmaz.
 * Ses + görüntü yalnızca DTLS-SRTP ile korunur.
 */

/**
 * Üst katman medya AES (Insertable Streams) — KAPALI.
 * Ses + görüntü yalnızca WebRTC DTLS-SRTP ile korunur.
 * true yapılmadan önce keyframe-kilitli ack protokolü doğrulanmalı.
 */
export const MEDIA_FRAME_ENCRYPTION_ENABLED = false;

/** @deprecated MEDIA_FRAME_ENCRYPTION_ENABLED kullanın */
export const MEDIA_FRAME_ENCRYPTION_SEND_ENABLED = MEDIA_FRAME_ENCRYPTION_ENABLED;

const MAGIC = new Uint8Array([0x54, 0x42, 0x4b, 0x4d]); // "TBKM"
const VERSION = 1;
const IV_LENGTH = 12;
const HEADER_LENGTH = MAGIC.length + 1 + IV_LENGTH; // 17

export interface MediaKeyHolder {
  rawB64: string | null;
  /** Rotasyon geçişinde eski anahtarlar — alım tarafında sırayla denenir */
  historyB64?: string[];
}

/** Peer başına gönderim kapısı: karşı taraf anahtarı ack'leyince açılır. */
export interface MediaEncryptionGate {
  enabled: boolean;
}

export function supportsMediaFrameEncryption(): boolean {
  if (typeof RTCRtpSender === 'undefined') return false;
  return typeof (RTCRtpSender.prototype as { createEncodedStreams?: unknown }).createEncodedStreams ===
    'function';
}

function getSubtle(): SubtleCrypto {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error('Web Crypto API kullanılamıyor (HTTPS gerekli)');
  }
  return subtle;
}

function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

const keyCache = new Map<string, Promise<CryptoKey>>();

function importAesKey(rawB64: string): Promise<CryptoKey> {
  let pending = keyCache.get(rawB64);
  if (!pending) {
    pending = getSubtle().importKey('raw', fromBase64(rawB64), { name: 'AES-GCM' }, false, [
      'encrypt',
      'decrypt',
    ]);
    keyCache.set(rawB64, pending);
  }
  return pending;
}

function toArrayBuffer(view: ArrayBuffer | ArrayBufferView): ArrayBuffer {
  if (view instanceof ArrayBuffer) return view;
  return view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength) as ArrayBuffer;
}

function frameBytes(data: ArrayBuffer | ArrayBufferView): Uint8Array {
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
}

export function hasEncryptedMediaHeader(bytes: Uint8Array): boolean {
  if (bytes.length < HEADER_LENGTH + 16) return false;
  for (let i = 0; i < MAGIC.length; i += 1) {
    if (bytes[i] !== MAGIC[i]) return false;
  }
  return bytes[MAGIC.length] === VERSION;
}

/** Ham çerçeve baytlarını AES-256-GCM ile paketler. */
export async function encryptMediaFramePayload(
  plaintext: ArrayBuffer | ArrayBufferView,
  rawB64: string
): Promise<ArrayBuffer> {
  const key = await importAesKey(rawB64);
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const plain = frameBytes(plaintext);
  const ciphertext = new Uint8Array(
    await getSubtle().encrypt({ name: 'AES-GCM', iv }, key, plain)
  );
  const out = new Uint8Array(HEADER_LENGTH + ciphertext.length);
  out.set(MAGIC, 0);
  out[MAGIC.length] = VERSION;
  out.set(iv, MAGIC.length + 1);
  out.set(ciphertext, HEADER_LENGTH);
  return out.buffer;
}

async function tryDecrypt(
  bytes: Uint8Array,
  rawB64: string
): Promise<ArrayBuffer | null> {
  try {
    const key = await importAesKey(rawB64);
    const iv = bytes.subarray(MAGIC.length + 1, HEADER_LENGTH);
    const ciphertext = bytes.subarray(HEADER_LENGTH);
    return await getSubtle().decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
  } catch {
    return null;
  }
}

/**
 * Şifreli çerçeveyi çözer. Magic yoksa (şifresiz peer) orijinal veriyi döner.
 * Aktif anahtar + rotasyon tarihçesi sırayla denenir; hiçbiri tutmazsa null
 * (ciphertext asla dekoder'e basılmamalı — karlı görüntü nedeni).
 */
export async function decryptMediaFramePayload(
  payload: ArrayBuffer | ArrayBufferView,
  rawB64: string | null,
  historyB64?: string[]
): Promise<ArrayBuffer | null> {
  const bytes = frameBytes(payload);
  if (!hasEncryptedMediaHeader(bytes)) {
    return toArrayBuffer(payload);
  }
  if (rawB64) {
    const out = await tryDecrypt(bytes, rawB64);
    if (out) return out;
  }
  if (historyB64) {
    for (const candidate of historyB64) {
      if (candidate === rawB64) continue;
      const out = await tryDecrypt(bytes, candidate);
      if (out) return out;
    }
  }
  return null;
}

type EncodedFrame = {
  data: ArrayBuffer;
  /** RTCEncodedVideoFrame: 'key' | 'delta' — ses karelerinde yoktur */
  type?: string;
};

type EncodedStreams = {
  readable: ReadableStream;
  writable: WritableStream;
};

function getEncodedStreams(endpoint: RTCRtpSender | RTCRtpReceiver): EncodedStreams | null {
  const create = (
    endpoint as { createEncodedStreams?: () => EncodedStreams }
  ).createEncodedStreams;
  if (typeof create !== 'function') return null;
  try {
    return create.call(endpoint);
  } catch (error) {
    console.warn('[TBKMeet] Insertable Streams açılamadı:', error);
    return null;
  }
}

/**
 * Video için şifreleme modu yalnızca KEYFRAME sınırında değişir.
 * GOP ortasında şifreli→şifresiz (veya tersi) geçiş dekoderi bozar → karlı görüntü.
 */
function pipeEncryptTransform(
  streams: EncodedStreams,
  keyHolder: MediaKeyHolder,
  gate: MediaEncryptionGate
) {
  let encrypting = false;
  const transform = new TransformStream<EncodedFrame, EncodedFrame>({
    async transform(frame, controller) {
      const rawB64 = keyHolder.rawB64;
      const wantEncrypt =
        MEDIA_FRAME_ENCRYPTION_ENABLED && gate.enabled && !!rawB64 && !!frame.data;
      const isVideo = typeof frame.type === 'string';

      if (!frame.data) {
        controller.enqueue(frame);
        return;
      }

      if (isVideo && wantEncrypt !== encrypting) {
        if (frame.type !== 'key') {
          // Mod değişimini keyframe'e ertele; mevcut modda devam et
          if (encrypting && rawB64) {
            try {
              frame.data = await encryptMediaFramePayload(frame.data, rawB64);
            } catch (error) {
              console.warn('[TBKMeet] Medya çerçeve şifreleme hatası:', error);
            }
          }
          controller.enqueue(frame);
          return;
        }
        encrypting = wantEncrypt;
      } else if (!isVideo) {
        encrypting = wantEncrypt;
      }

      if (encrypting && rawB64) {
        try {
          frame.data = await encryptMediaFramePayload(frame.data, rawB64);
        } catch (error) {
          console.warn('[TBKMeet] Medya çerçeve şifreleme hatası:', error);
        }
      }
      controller.enqueue(frame);
    },
  });
  streams.readable.pipeThrough(transform).pipeTo(streams.writable).catch((error) => {
    console.warn('[TBKMeet] Medya encrypt pipe kapandı:', error);
  });
}

function pipeDecryptTransform(streams: EncodedStreams, keyHolder: MediaKeyHolder) {
  // Drop veya şifreli↔şifresiz geçiş sonrası GOP kırılır → keyframe bekle.
  let videoNeedsKeyframe = false;
  let sawEncrypted = false;
  const transform = new TransformStream<EncodedFrame, EncodedFrame>({
    async transform(frame, controller) {
      if (!frame.data) {
        controller.enqueue(frame);
        return;
      }
      const isVideo = typeof frame.type === 'string';
      const bytes = frameBytes(frame.data);
      const encrypted = hasEncryptedMediaHeader(bytes);

      try {
        if (encrypted) {
          sawEncrypted = true;
          const decrypted = await decryptMediaFramePayload(
            frame.data,
            keyHolder.rawB64,
            keyHolder.historyB64
          );
          if (decrypted == null) {
            if (isVideo) videoNeedsKeyframe = true;
            warnDecryptThrottled(
              '[TBKMeet] Medya çerçeve çözülemedi (anahtar yok veya uyumsuz); çerçeve atıldı'
            );
            return;
          }
          if (isVideo && videoNeedsKeyframe && frame.type !== 'key') {
            return;
          }
          videoNeedsKeyframe = false;
          frame.data = decrypted;
          controller.enqueue(frame);
          return;
        }

        // Şifresiz kare: daha önce şifreli akış gördüysek GOP karışmasın diye
        // keyframe'e kadar bekle (gönderici AES'i kapattığında oluşur).
        if (isVideo && sawEncrypted) {
          if (frame.type !== 'key') {
            videoNeedsKeyframe = true;
            return;
          }
          sawEncrypted = false;
          videoNeedsKeyframe = false;
        } else if (isVideo && videoNeedsKeyframe) {
          if (frame.type !== 'key') return;
          videoNeedsKeyframe = false;
        }

        controller.enqueue(frame);
      } catch (error) {
        if (isVideo) videoNeedsKeyframe = true;
        warnDecryptThrottled('[TBKMeet] Medya çerçeve çözme hatası:', error);
      }
    },
  });
  streams.readable.pipeThrough(transform).pipeTo(streams.writable).catch((error) => {
    console.warn('[TBKMeet] Medya decrypt pipe kapandı:', error);
  });
}

const attachedSenders = new WeakSet<RTCRtpSender>();
const attachedReceivers = new WeakSet<RTCRtpReceiver>();
let lastDecryptWarnAt = 0;

function warnDecryptThrottled(message: string, error?: unknown) {
  const now = Date.now();
  if (now - lastDecryptWarnAt < 5000) return;
  lastDecryptWarnAt = now;
  if (error !== undefined) console.warn(message, error);
  else console.warn(message);
}

/** Göndericiye AES-256 çerçeve şifrelemesi bağlar (bir kez, gate kontrollü). */
export function attachSenderMediaEncryption(
  sender: RTCRtpSender,
  keyHolder: MediaKeyHolder,
  gate: MediaEncryptionGate
): boolean {
  if (!MEDIA_FRAME_ENCRYPTION_ENABLED) return false;
  if (!sender.track || attachedSenders.has(sender)) return false;
  const streams = getEncodedStreams(sender);
  if (!streams) return false;
  attachedSenders.add(sender);
  pipeEncryptTransform(streams, keyHolder, gate);
  return true;
}

/** Alıcıya AES-256 çerçeve çözme bağlar (bir kez). */
export function attachReceiverMediaDecryption(
  receiver: RTCRtpReceiver,
  keyHolder: MediaKeyHolder
): boolean {
  if (!MEDIA_FRAME_ENCRYPTION_ENABLED) return false;
  if (attachedReceivers.has(receiver)) return false;
  const streams = getEncodedStreams(receiver);
  if (!streams) return false;
  attachedReceivers.add(receiver);
  pipeDecryptTransform(streams, keyHolder);
  return true;
}

/** PeerConnection üzerindeki tüm sender'lara şifreleme uygular (gate kontrollü). */
export function attachPeerMediaEncryption(
  pc: RTCPeerConnection,
  keyHolder: MediaKeyHolder,
  gate: MediaEncryptionGate
): number {
  if (!MEDIA_FRAME_ENCRYPTION_ENABLED || !supportsMediaFrameEncryption()) return 0;
  let count = 0;
  pc.getSenders().forEach((sender) => {
    if (attachSenderMediaEncryption(sender, keyHolder, gate)) count += 1;
  });
  return count;
}

/** RTCPeerConnection için encodedInsertableStreams bayrağı. */
export function withInsertableStreamsConfig(config: RTCConfiguration): RTCConfiguration {
  if (!MEDIA_FRAME_ENCRYPTION_ENABLED || !supportsMediaFrameEncryption()) return config;
  return {
    ...config,
    encodedInsertableStreams: true,
  } as RTCConfiguration;
}
